import ApiService from "@Src/services/ApiService";
import { TEST_DETAILS_API_ROUTES } from "./TestDetails.api.constant";
import { store } from "@Src/app/store";

/**
 * Helper to enrich payload with languageId from user preferences if logged in
 */
const enrichWithLanguage = (data: any) => {
    try {
        const state = store.getState();
        const loginData = (state as any).login;
        const isLoggedIn = loginData?.isLoggedIn;
        const languageId = loginData?.LanguageId;

        if (isLoggedIn && languageId && (!data.languageId || (Array.isArray(data.languageId) && data.languageId.length === 0))) {
            return {
                ...data,
                languageId: [languageId]
            };
        }
    } catch (error) {
        console.error('Error enriching payload with language:', error);
    }
    return data;
};

export async function getSimilarTests(params: any) {
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.DETAILS + '/' + TEST_DETAILS_API_ROUTES.SIMILAR_TEST,
        method: 'get',
        params: params
    })
}

export async function getTestDetails(data: any) {
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.DETAILS + '/' + TEST_DETAILS_API_ROUTES.TEST_DETAILS,
        method: 'post',
        data
    })
}

export async function getTestList(data: any) {
    const enrichedData = enrichWithLanguage(data);
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.DETAILS + '/' + TEST_DETAILS_API_ROUTES.TEST_LIST,
        method: 'post',
        data: enrichedData
    })
}

export async function getSearchFilters(data: any) {
    const enrichedData = enrichWithLanguage(data);
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.SEARCH_FILTERS,
        method: 'post',
        data: enrichedData
    })
}

export async function getDifficultyLevels() {
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.DIFFICULTY_LEVEL,
        method: 'get',
        params: ''
    })
}


export async function getTestSeriesList(data: any) {
    // Build payload using user's course preferences and language
    const enhancedData = (() => {
        // If testSeriesId is already provided, keep payload as-is
        if (data && data.testSeriesId) {
            return { ...data };
        }
        try {
            const state = store.getState();
            const coursePrefData = state.coursePreferences;
            const loginData = (state as any).login;

            const boardId = (coursePrefData?.boardDetails?.[0] as any)?.Id;
            const classId = (coursePrefData?.classDetails?.[0] as any)?.Id;
            const languageId = loginData?.LanguageId;
            const subjectIds = coursePrefData?.subjectDetails?.map((s: any) => s.Id) || [];

            // Determine effective board ID to check for match
            const effectiveBoardId = data.boardId || boardId;
            const isBoardMatch = String(effectiveBoardId) === String(boardId);

            return {
                ...(boardId && !data.boardId ? { boardId } : {}),
                ...(classId && !data.classId ? { classId } : {}),
                // Only auto-add languageId if user is logged in and not already provided
                ...(loginData?.isLoggedIn && languageId && (!data.languageId || (Array.isArray(data.languageId) && data.languageId.length === 0)) ? { languageId: [languageId] } : {}),
                // Add subjectId array from coursePreferences ONLY if board matches
                ...(subjectIds.length > 0 && !data.subjectId && isBoardMatch ? { subjectId: subjectIds } : {}),
                ...data,
            };
        } catch (error) {
            // If any of the supporting calls fail, fall back to the provided payload
            return { ...data };
        }
    })();

    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.TEST_SERIES_LIST,
        method: 'post',
        data: enhancedData
    })
}

export async function getTestSeriesListAll(data: any) {
    // Build payload using user's course preferences and language
    const enhancedData = (() => {
        // If testSeriesId is already provided, keep payload as-is
        if (data && data.testSeriesId) {
            return { ...data };
        }
        try {
            const state = store.getState();
            const coursePrefData = state.coursePreferences;
            const loginData = (state as any).login;

            const boardId = (coursePrefData?.boardDetails?.[0] as any)?.Id;
            const classId = (coursePrefData?.classDetails?.[0] as any)?.Id;
            const languageId = loginData?.LanguageId;
            const subjectIds = coursePrefData?.subjectDetails?.map((s: any) => s.Id) || [];

            // Determine effective board ID to check for match
            const effectiveBoardId = data.boardId || boardId;
            const isBoardMatch = String(effectiveBoardId) === String(boardId);

            return {
                ...(boardId && !data.boardId ? { boardId } : {}),
                ...(classId && !data.classId ? { classId } : {}),
                // Only auto-add languageId if user is logged in and not already provided
                ...(loginData?.isLoggedIn && languageId && (!data.languageId || (Array.isArray(data.languageId) && data.languageId.length === 0)) ? { languageId: [languageId] } : {}),
                // Add subjectId array from coursePreferences ONLY if board matches
                ...(subjectIds.length > 0 && !data.subjectId && isBoardMatch ? { subjectId: subjectIds } : {}),
                ...data,
            };
        } catch (error) {
            // If any of the supporting calls fail, fall back to the provided payload
            return { ...data };
        }
    })();

    // Decision logic for URL based on login status and page context
    const state = store.getState();
    const loginData = (state as any).login;


    const url = (loginData?.isLoggedIn)
        ? TEST_DETAILS_API_ROUTES.TEST_SERIES_LIST
        : TEST_DETAILS_API_ROUTES.TEST_SERIES_LIST_ALL;

    return await ApiService.fetchData({
        url: url,
        method: 'post',
        data: enhancedData
    })
}
export async function setIabhyasTestSeries() {
  
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.IABHYAS_TESTS,
        method: 'post',
    })
}

export async function getTestSeriesSearchFilters(data: any) {
    const enrichedData = enrichWithLanguage(data);
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.TEST_SERIES_FILTERS,
        method: 'post',
        data: enrichedData
    })
}

export async function getTestSeriesDetails(data: any) {
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.DETAILS + '/' + TEST_DETAILS_API_ROUTES.EXAM_DETAILS,
        method: 'post',
        data
    })
}

export async function getTestSeriesListByClass(classId: any) {
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.TEST_SERIES_BY_CLASS,
        method: 'get',
        params: { classId }
    })
}

export async function getPreviousOpenStateTest() {
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.TEST + '/' + TEST_DETAILS_API_ROUTES.PREVIOUS_OPEN_TEST,
        method: 'get',
        data: {}
    })
}

export async function putEndPreviousOpenStateTests() {
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.TEST + '/' + TEST_DETAILS_API_ROUTES.END_PREVIOUS_OPEN_TEST,
        method: 'put',
        data: {}
    })
}

export async function getChapterList(params: any) {
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.CHAPTER_LIST,
        method: 'get',
        params
    })
}

export async function getTopicList(params: any) {
    return await ApiService.fetchData({
        url: TEST_DETAILS_API_ROUTES.TOPIC_LIST,
        method: 'get',
        params
    })
}

export async function getExamQuestions(params: any) {
    return await ApiService.fetchData({
        url: 'mock/' + TEST_DETAILS_API_ROUTES.TEST + '/' + TEST_DETAILS_API_ROUTES.EXAM_QUESTION,
        method: 'get',
        params
    })
}

export async function submitExamAnswer(data: any) {
    return await ApiService.fetchData({
        url: 'mock/' + TEST_DETAILS_API_ROUTES.TEST + '/' + TEST_DETAILS_API_ROUTES.EXAM_QUESTION,
        method: 'put',
        data
    })
}
